import React, { useState, useEffect } from 'react';
import { trackFormSubmit } from '../utils/gtag';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Search, Filter, ArrowRight } from 'lucide-react';
import BlogCard from '../components/BlogCard';
import CTA from '../components/CTA';
import FreeGuideCTA from '../components/FreeGuideCTA';
import { fetchBlogPosts, BlogPost } from '../utils/blogPosts';

const Blog: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const loadPosts = async () => {
      const posts = await fetchBlogPosts();
      setBlogPosts(posts);
      setLoading(false);
    };
    loadPosts();
  }, []);

  // Get categories from blog posts
  const categories = ['All', ...Array.from(new Set(blogPosts.map(post => post.category)))];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  if (loading) {
    return (
      <main>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600"></div>
        </div>
      </main>
    );
  }

  return (
    <>
      <Helmet>
        <title>Hypnotherapy Blog | Mental Health Tips & Success Stories | Northern Ireland</title>
        <meta 
          name="description" 
          content="Expert hypnotherapy insights, mental health tips, and client success stories from Sharon Clare. Learn about anxiety treatment, trauma recovery, stress management, and more." 
        />
        <meta name="keywords" content="hypnotherapy blog, mental health tips, anxiety help, stress management, hypnotherapy success stories, Belfast therapist blog, Northern Ireland hypnotherapy blog" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/blog/" />
        <meta property="og:title" content="Hypnotherapy Blog | Mental Health Tips & Success Stories" />
        <meta property="og:description" content="Expert hypnotherapy insights, mental health tips, and client success stories from Sharon Clare in Belfast, Lisburn & Northern Ireland." />
        <meta property="og:url" content="https://www.sharonclare-hypnotherapy.com/blog/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Hypnotherapy Blog | Mental Health Tips & Success Stories" />
        <meta name="twitter:description" content="Expert hypnotherapy insights, mental health tips, and client success stories from Sharon Clare." />
      </Helmet>
      
      <main>
      {/* Hero Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto px-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4 sm:mb-6">
              Hypnotherapy Blog
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
              Insights, tips, and success stories to support your mental health and well-being journey. 
              Discover how <a href="/services" className="text-accent-600 hover:text-accent-700 underline">clinical hypnotherapy</a> can transform your life. <a href="/about" className="text-accent-600 hover:text-accent-700 underline">Learn more about our approach</a>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 inline-flex items-center justify-center"
              >
                Request a 10-Minute Call
                <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 ml-2" />
              </Link>
              <a
                href="tel:07932139245"
                className="btn-secondary text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 inline-flex items-center justify-center"
              >
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 sm:py-12 bg-white border-b border-gray-200">
        <div className="container-custom px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-3 sm:gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 sm:pl-11 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 text-sm sm:text-base"
                />
              </div>
              <div className="relative md:w-56">
                <Filter className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full pl-9 sm:pl-11 pr-8 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 appearance-none bg-white text-sm sm:text-base"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredPost && (
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="container-custom px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6 sm:mb-8 text-center">
                Featured Article
              </h2>
              <BlogCard 
                id={featuredPost.slug}
                title={featuredPost.title}
                excerpt={featuredPost.excerpt}
                author={featuredPost.author}
                publishedAt={featuredPost.publishedAt}
                readTime={featuredPost.readTime}
                category={featuredPost.category}
                image={featuredPost.image}
                tags={featuredPost.tags}
                featured={featuredPost.featured}
              />
            </div>
          </div>
        </section>
      )}

      {/* Blog Posts Grid */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="container-custom px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6 sm:mb-8 pb-4 border-b-2 border-gray-200">
              Latest Articles
            </h2>
            
            {regularPosts.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                {regularPosts.map((post) => (
                  <BlogCard 
                    key={post.id} 
                    id={post.slug}
                    title={post.title}
                    excerpt={post.excerpt}
                    author={post.author}
                    publishedAt={post.publishedAt}
                    readTime={post.readTime}
                    category={post.category}
                    image={post.image}
                    tags={post.tags}
                    featured={post.featured}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 sm:py-20">
                <p className="text-gray-600 text-base sm:text-lg">
                  No articles found matching your search criteria.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Free Guide Lead Magnet */}
      <FreeGuideCTA location="Blog Index" />

      {/* Newsletter Signup */}
      <section className="py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Stay Updated
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Get the latest insights on mental health, hypnotherapy techniques, and wellness tips delivered to your inbox.
            </p>
            <form
              className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget as HTMLFormElement;
                const formData = new FormData(form);
                const email = String(formData.get('email') || '').trim();
                if (!email) return;
                try {
                  const res = await fetch('/api/subscribe', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, list: 'newsletter', source: 'blog-page' })
                  });
                  const data = await res.json().catch(() => ({} as any));
                  if (res.ok) {
                    trackFormSubmit('newsletter', 'newsletter');
                    window.location.href = '/thank-you?from=newsletter-subscribe';
                  } else {
                    alert((data && data.error) || 'Sorry, something went wrong. Please try again later.');
                  }
                } catch (err) {
                  alert('Network error. Please try again.');
                }
              }}
            >
              <input
                name="email"
                type="email"
                required
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
              />
              <button type="submit" className="btn-primary px-8 py-3">
                Subscribe
              </button>
            </form>
            <p className="text-sm text-gray-500 mt-4">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Ready to Start Your Transformation?"
        subtitle="Book your free 10-minute consultation today and take the first step towards better mental health and well-being."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
    </>
  );
};

export default Blog;
